import React, { useState, useEffect } from 'react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts';
import ONCRP_TOOLS_QP_Card from './OncoRiskCard'; // Adjust path if necessary
import './TierDecisionCard.css';

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const label = payload[0].payload.subject.replace(/\n/g, ' ');
        return (
            <div className="ONCRP-TOOLS-QP-chart-tooltip">
                <p className="ONCRP-TOOLS-QP-tooltip-label">{label}</p>
                <p className="ONCRP-TOOLS-QP-tooltip-score">Score: {payload[0].value.toFixed(1)}</p>
            </div>
        );
    }
    return null;
};

// This component now adds vertical distance for the single-line labels
const CustomAxisTick = (props) => {
    const { x, y, payload, chartColors } = props;
    const { value } = payload; 

    let textAnchor;
    let dx = 0;
    let dy = 0; // Vertical offset, defaults to 0
    const PADDING = 5;
    const VERTICAL_PADDING = 15; // Extra distance for top/bottom labels

    switch (value) {
        case 'Clinical Significance':
            textAnchor = 'middle';
            dy = -VERTICAL_PADDING; // Push the top label up
            break;
        case 'Evidence Strength':
            textAnchor = 'middle';
            dy = VERTICAL_PADDING; // Push the bottom label down
            break;
        case 'Population\nRarity':
        case 'Computational\nPrediction':
            textAnchor = 'end';
            dx = -PADDING;
            break;
        default:
            textAnchor = 'start';
            dx = PADDING;
            break;
    }

    const words = value.split('\n');
  
    return (
      // The group is now translated with both horizontal and vertical offsets
      <g transform={`translate(${x + dx},${y + dy})`}>
        <text textAnchor={textAnchor} fill={chartColors?.axisText || "#a9b8d4"} fontSize={12}>
          {words.map((word, index) => (
            <tspan key={index} x={0} dy={index === 0 ? '0.355em' : '1.2em'}>
              {word}
            </tspan>
          ))}
        </text>
      </g>
    );
};

const getTierNumber = (tierString) => {
    if (!tierString) return null;
    const upperTierString = tierString.toUpperCase();
    const romanMap = { 'I': 1, 'II': 2, 'III': 3, 'IV': 4 };
    
    for (const roman in romanMap) {
        if (upperTierString.includes(roman)) {
            return romanMap[roman];
        }
    }
    
    const arabicMatch = upperTierString.match(/\d+/);
    if (arabicMatch) {
        return parseInt(arabicMatch[0], 10);
    }
    
    return null;
};

const TierDecisionCard = ({ tierDecision }) => {
    const [activeCategory, setActiveCategory] = useState(null);
    const [isLightTheme, setIsLightTheme] = useState(false);

    useEffect(() => {
        if (tierDecision?.categories?.length > 0 && !activeCategory) {
            setActiveCategory(tierDecision.categories[0].category);
        }
    }, [tierDecision, activeCategory]);

    useEffect(() => {
        const checkTheme = () => {
            if (typeof document !== 'undefined') {
                const hasLightClass = document.documentElement.classList.contains('ocrp-theme-light');
                setIsLightTheme(hasLightClass);
            }
        };

        checkTheme();

        if (typeof window !== 'undefined') {
            const handleThemeChange = (event) => {
                if (event.detail && typeof event.detail.theme === 'string') {
                    setIsLightTheme(event.detail.theme === 'light');
                }
            };

            window.addEventListener('ocrp-theme-change', handleThemeChange);
            return () => window.removeEventListener('ocrp-theme-change', handleThemeChange);
        }
    }, []);

    if (!tierDecision) {
        return (
            <ONCRP_TOOLS_QP_Card noHeader className="ONCRP-TOOLS-QP-span-col-6">
                <div className="ONCRP-TOOLS-QP-tier-card-placeholder">
                    <p>Awaiting tiering information...</p>
                </div>
            </ONCRP_TOOLS_QP_Card>
        );
    }

    const { final_tier = '', summary, categories, overall_score } = tierDecision;
    const tierNumber = getTierNumber(final_tier);

    const chartData = categories.map(cat => {
        const shouldBreak = cat.category !== 'Clinical Significance' && cat.category !== 'Evidence Strength';
        return {
            subject: shouldBreak ? cat.category.replace(/ /g, '\n') : cat.category,
            score: cat.score,
            fullMark: 5,
        };
    });

    const activeBreakdown = categories.find(cat => cat.category === activeCategory);

    const getReasonClassName = (reason) => {
        let classNames = "ONCRP-TOOLS-QP-reason-item";
        if (reason.strong) classNames += " ONCRP-TOOLS-QP-reason-item--strong";
        if (reason.isConflict) classNames += " ONCRP-TOOLS-QP-reason-item--conflict";
        return classNames;
    };

    // Theme-aware chart colors
    const chartColors = {
        grid: isLightTheme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.15)',
        stroke: isLightTheme ? '#3b82f6' : '#FFFFFF',
        fillGradientStart: isLightTheme ? '#3b82f6' : '#FFFFFF',
        fillGradientEnd: isLightTheme ? '#1d4ed8' : '#FFFFFF',
        axisText: isLightTheme ? '#374151' : '#a9b8d4',
        tooltip: {
            cursor: isLightTheme ? 'rgba(59, 130, 246, 0.3)' : 'rgba(209, 217, 230, 0.5)'
        }
    };

    return (
        <ONCRP_TOOLS_QP_Card 
        title="Automated Variant Tier Classifier" icon="fa-brain"
        className={`ONCRP-TOOLS-QP-span-col-6 ONCRP-TOOLS-QP-tier-decision-card`}
        >
            {/* --- STAGE 1: THE VERDICT (HEADER) --- */}
            <div className="ONCRP-TOOLS-QP-profile-header">
                <div className="ONCRP-TOOLS-QP-header-main">
                    <div className="ONCRP-TOOLS-QP-final-tier-group">
                        <span className="ONCRP-TOOLS-QP-final-tier-label">Final Tier</span>
                        <h2 className={`ONCRP-TOOLS-QP-final-tier-value tier-color-${tierNumber}`}>{final_tier}</h2>
                    </div>
                    <p className="ONCRP-TOOLS-QP-tier-summary-text">{summary}</p>
                </div>
                <div className="ONCRP-TOOLS-QP-header-score">
                    <span className="ONCRP-TOOLS-QP-overall-score-value">{overall_score.toFixed(2)}</span>
                    <span className="ONCRP-TOOLS-QP-overall-score-label">Overall Score</span>
                </div>
            </div>

            {/* --- STAGE 2: THE EVIDENCE PROFILE (CHART + CATEGORIES) --- */}
            <div className="ONCRP-TOOLS-QP-profile-body">
                <div className="ONCRP-TOOLS-QP-chart-container">
                    <ResponsiveContainer width="100%" height="100%">
                        <RadarChart cx="50%" cy="50%" outerRadius="67%" data={chartData}>
                            <defs>
                                <linearGradient id="ONCRP_TOOLS_QP_radarFill" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={chartColors.fillGradientStart} stopOpacity={0.25}/>
                                    <stop offset="95%" stopColor={chartColors.fillGradientEnd} stopOpacity={0.1}/>
                                </linearGradient>
                            </defs>
                            <PolarGrid stroke={chartColors.grid} />
                            <PolarAngleAxis dataKey="subject" tick={<CustomAxisTick chartColors={chartColors} />} />
                            <PolarRadiusAxis angle={30} domain={[0, 5]} tick={false} axisLine={false} />
                            <Tooltip content={<CustomTooltip />} cursor={{ stroke: chartColors.tooltip.cursor, strokeWidth: 1, strokeDasharray: '3 3' }} />
                            <Radar name="Score" dataKey="score" stroke={chartColors.stroke} strokeWidth={2} fill="url(#ONCRP_TOOLS_QP_radarFill)" fillOpacity={0.9} />
                        </RadarChart>
                    </ResponsiveContainer>
                </div>
                <div className="ONCRP-TOOLS-QP-categories-container">
                    <ul className="ONCRP-TOOLS-QP-categories-list">
                        {categories.map((cat) => (
                            <li 
                                key={cat.category}
                                onMouseEnter={() => setActiveCategory(cat.category)}
                                className={`ONCRP-TOOLS-QP-category-item ${activeCategory === cat.category ? 'ONCRP-TOOLS-QP-category-item--active' : ''}`}
                            >
                                <div className="ONCRP-TOOLS-QP-category-text">
                                    <span className="ONCRP-TOOLS-QP-category-name">{cat.category}</span>
                                    <span className="ONCRP-TOOLS-QP-category-score">{cat.score.toFixed(1)}</span>
                                </div>
                                <div className="ONCRP-TOOLS-QP-score-bar-background">
                                    <div className="ONCRP-TOOLS-QP-score-bar" style={{ width: `${(cat.score / 5) * 100}%` }}></div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* --- STAGE 3: THE DEEP DIVE (BREAKDOWN) --- */}
            {activeBreakdown && (
                <div className="ONCRP-TOOLS-QP-breakdown-footer">
                     <h4 className="ONCRP-TOOLS-QP-details-title">
                        Evidence Breakdown: <span className="ONCRP-TOOLS-QP-details-title--highlight">{activeBreakdown.category}</span>
                    </h4>
                    <ul className="ONCRP-TOOLS-QP-reasons-list">
                        {activeBreakdown.breakdown.length > 0 ? activeBreakdown.breakdown.map((reason, index) => (
                            <li key={index} className={getReasonClassName(reason)}>{reason.text}</li>
                        )) : (
                            <li className="ONCRP-TOOLS-QP-reason-item ONCRP-TOOLS-QP-reason-item--no-evidence">
                                No specific evidence points were recorded for this category.
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </ONCRP_TOOLS_QP_Card>
    );
};

export default TierDecisionCard;